import React, { memo, useMemo } from 'react';
import Highcharts, { Point, Series, SeriesPieOptions, TooltipFormatterContextObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import GraphTheme from '@/graphTheme';
import { decimalFormatter, percentFormatter } from '@/utils/numberFormatter';
import type { TPieHighStockProps, TChartDataPoint, TProcessedDataItem } from '@/screen/Portfolios/Summary/types/PortfolioTypes';

function tooltipFormatter(this: TooltipFormatterContextObject): string
{
    return this.key.toString();
}

function legendLabelFormatter(this: Point | Series): string
{
    if('percentage' in this)
    {
        if(this.percentage)
        {
            const point = this as Highcharts.Point;
            if(typeof point.percentage === 'number' && typeof point.y === 'number')
            {
                const percentage = point.percentage;
                const formattedPercentage = `${decimalFormatter.format(percentage)}%`;
                return `${point.name.replace(/\s%/, "%")} (${formattedPercentage})`;
            }
            return point.name;
        }
    }
    return this.name;
}

const NO_DATA_MESSAGE = 'No hay datos para mostrar el gráfico';

const PieHighStock: React.FC<TPieHighStockProps> = memo(({chartSerie, showSubcategories, showNoDataMessage = false}) =>
{
    const {browserData, versionsData} = useMemo(() =>
    {
        const colors = GraphTheme.colors;
        let count = 0;
        const browserDataResult: TChartDataPoint[] = [];
        const versionsDataResult: TChartDataPoint[] = [];
        const categories: string[] = [];
        const data: TProcessedDataItem[] = [];

        if(chartSerie && chartSerie.length !== undefined && chartSerie.length > 0)
        {
            for(const entry of chartSerie)
            {
                const drilldownCategories: string[] = [];
                const drilldownData: number[] = [];
                if(entry.level2)
                {
                    entry.level2.forEach((level2Item) =>
                    {
                        drilldownCategories.push(`${level2Item.name} ${percentFormatter.format(level2Item.value/100)}`);
                        drilldownData.push(level2Item.value);
                    });

                    data.push(
                    {
                        color: colors[count],
                        y: entry.value,
                        drilldown:
                        {
                            name: `${entry.name} ${percentFormatter.format(entry.value/100)}`,
                            categories: drilldownCategories,
                            data: drilldownData,
                        }
                    });

                    categories.push(`${entry.name} ${percentFormatter.format(entry.value/100)}`);
                    count += 1;
                }
            }
        }

        const dataLen = data.length;
        for(let i = 0; i < dataLen; i += 1)
        {
            browserDataResult.push(
            {
                name: categories[i],
                y: data[i].y,
                color: data[i].color,
            });

            const drillDataLen = data[i].drilldown.data.length;
            for(let j = 0; j < drillDataLen; j += 1)
            {
                const brightness = 0.2 - j / drillDataLen / 5;
                const color = Highcharts.color(data[i].color).brighten(brightness).get();
                if(typeof color === 'string')
                {
                    versionsDataResult.push(
                    {
                        name: data[i].drilldown.categories[j],
                        y: data[i].drilldown.data[j],
                        color
                    });
                }
            }
        }

        return { browserData: browserDataResult, versionsData: versionsDataResult };
    }, [chartSerie]);

    const defaultOptions = useMemo<Highcharts.Options>(() =>
    {
        const series: SeriesPieOptions[] =
        [
            {
                type: 'pie',
                name: 'Categoría',
                data: browserData,
                innerSize: '50%',
                showInLegend: true,
                dataLabels:
                {
                    crop: false,
                    distance: -30,
                },
            } as SeriesPieOptions,
        ];

        if(showSubcategories)
        {
            series.push(
            {
                type: 'pie',
                name: 'Subcategorías',
                data: versionsData,
                innerSize: '66%',
                size: '80%',
                dataLabels:
                {
                    crop: true,
                    overflow: 'allow',
                    padding: 3,
                    startAngle: 10,
                    distance: 5,
                    style:
                    {
                        textOverflow: 'clip',
                    }
                },
                id: 'versions'
            } as SeriesPieOptions);
        }
        return{
            chart:
            {
                type: 'pie',
                backgroundColor: 'none',
            },
            title:
            {
                text: ''
            },
            plotOptions:
            {
                pie:
                {
                    size: '110%',
                    dataLabels:
                    {
                        enabled: false,
                        style:
                        {
                            textOverflow: 'clip',
                            textOutline: 'none',
                        },
                    }
                }
            },
            credits:
            {
                enabled: false,
            },
            tooltip:
            {
                valueSuffix: '%',
                enabled: true,
                headerFormat: '',
                clusterFormat: undefined,
                footerFormat: '',
                backgroundColor: '#FFFFFF',
                borderRadius: 4,
                borderColor: '#FFFFFF',
                formatter: tooltipFormatter,
                followPointer: false,
                style:
                {
                    fontSize: '13px',
                    fontWeight: '600',
                    fontFamily: 'Barlow',
                },
            },
            legend:
            {
                layout: 'vertical',
                align: 'center',
                floating: false,
                x: -12,
                symbolHeight: 16,
                symbolRadius: 2,
                itemMarginTop: 12,
                itemStyle:
                {
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'Arial',
                    lineHeight: '20px',
                },
                labelFormatter: legendLabelFormatter,
            },
            series,
        };
    }, [browserData, versionsData, showSubcategories]);

    if(showNoDataMessage && (!chartSerie || chartSerie.length === 0))
    {
        return <div>{NO_DATA_MESSAGE}</div>;
    }

    return(
        <div>
            <HighchartsReact highcharts={Highcharts} options={{ ...GraphTheme, ...defaultOptions}} />
        </div>
    );
}, (prevProps, nextProps) => prevProps.showSubcategories === nextProps.showSubcategories && prevProps.showNoDataMessage === nextProps.showNoDataMessage && prevProps.chartSerie === nextProps.chartSerie);

export default PieHighStock;